import { ColumnDef, FilterFn, RowData, SortingFn } from '@tanstack/react-table';

import { Checkbox } from '@/new/components/shadcn/checkbox';

declare module '@tanstack/react-table' {
  // Type params must mirror the upstream ColumnMeta declaration for the augmentation
  // to merge, even though this added field does not reference them.
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    sortIcon?: 'string' | 'number'; // extending the column metadata to include the type of icon to use when sorting
  }
}

export const includedInArr: FilterFn<any> = (row, columnId, filterValue: string[]) => {
  if (!filterValue || filterValue.length === 0) return true;
  const cellValue = row.getValue<string>(columnId);
  return filterValue.some((filterVal) => filterVal.toLowerCase() === cellValue?.toLowerCase());
};

export const arrInArr: FilterFn<any> = (row, columnId, filterValue: string[]) => {
  if (!filterValue || filterValue.length === 0) return true;
  const cellValue = row.getValue<string[]>(columnId);
  if (!cellValue || cellValue.length === 0) return false;
  return cellValue.some((value) => filterValue.includes(value));
};

// global filter for selected rows
export const selectedRowsFilter: FilterFn<any> = (row, _columnId, filterValue) => {
  if (!filterValue) return true;
  return row.getIsSelected();
};

// custom sorting function to put empty/falsy values last
export const sortStringWithEmptyLast: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue<string>(columnId);
  const b = rowB.getValue<string>(columnId);

  // If both are falsy, they're equal
  if (!a && !b) return 0;
  // If only a is falsy, b comes first
  if (!a) return 1;
  // If only b is falsy, a comes first
  if (!b) return -1;

  // Both have values, do normal string comparison
  return a.localeCompare(b);
};

export const sortArrayLength: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue<unknown[]>(columnId);
  const b = rowB.getValue<unknown[]>(columnId);
  return a.length > b.length ? 1 : a.length < b.length ? -1 : 0;
};

export function formatDatetimeForTable(value: Date): string {
  return new Date(value).toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
}

export const selectColumn = <T,>(): ColumnDef<T> => ({
  id: 'select',
  header: ({ table }) => (
    <div className="flex w-full items-center justify-center text-center">
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
      />
    </div>
  ),
  cell: (info) => (
    <div className="flex w-full min-w-5 items-center justify-center text-center">
      <Checkbox
        checked={info.row.getIsSelected()}
        disabled={!info.row.getCanSelect()}
        onCheckedChange={info.row.getToggleSelectedHandler()}
      />
    </div>
  ),
});
